import { useCallback, useEffect, useState } from "react";
import { getDataClient } from "@/lib/dataClient";

export interface StudyDay {
  date: string;
  minutes: number;
}

export interface Task {
  id: string;
  title: string;
  detail: string | null;
  icon: string;
  completed: boolean;
}

export interface LiveClass {
  id: string;
  subject: string;
  title: string;
  teacher_name: string | null;
  starts_at: string;
}

export interface ExamResult {
  id: string;
  exam_name: string;
  correct_count: number;
  wrong_count: number;
  blank_count: number;
  net: number;
  taken_on: string;
}

export interface WeakTopic {
  id: string;
  subject: string;
  topic: string;
  mastery_percent: number;
}

export interface DashboardData {
  weekMinutes: number;
  studyDays: StudyDay[];
  tasks: Task[];
  liveClass: LiveClass | null;
  lastExam: ExamResult | null;
  weakTopics: WeakTopic[];
}

const EMPTY: DashboardData = {
  weekMinutes: 0,
  studyDays: [],
  tasks: [],
  liveClass: null,
  lastExam: null,
  weakTopics: [],
};

// Son 7 günün (bugün dahil) ISO tarih listesini döndürür.
function lastSevenDates(): string[] {
  const dates: string[] = [];
  const now = new Date();
  for (let i = 6; i >= 0; i -= 1) {
    const d = new Date(now);
    d.setDate(now.getDate() - i);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate(),
    ).padStart(2, "0")}`;
    dates.push(iso);
  }
  return dates;
}

export function useStudentDashboard() {
  const [data, setData] = useState<DashboardData>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const dataClient = getDataClient();

      const { data: authData } = await dataClient.auth.getUser();
      const user = authData.user;
      if (!user) {
        setData(EMPTY);
        setLoading(false);
        return;
      }

      const userId = user.id;
      const dates = lastSevenDates();
      const startDate = dates[0];
      const today = dates[6];

      // 1) Haftalık çalışma oturumları
      const { data: sessions, error: sessionErr } = await dataClient
        .from("study_sessions")
        .select("studied_on, minutes")
        .eq("user_id", userId)
        .gte("studied_on", startDate)
        .lte("studied_on", today);

      if (sessionErr) throw sessionErr;

      const minuteMap = new Map<string, number>();
      (sessions ?? []).forEach((s: { studied_on: string; minutes: number }) => {
        minuteMap.set(s.studied_on, (minuteMap.get(s.studied_on) ?? 0) + s.minutes);
      });
      const studyDays: StudyDay[] = dates.map((d) => ({
        date: d,
        minutes: minuteMap.get(d) ?? 0,
      }));
      const weekMinutes = studyDays.reduce((sum, d) => sum + d.minutes, 0);

      // 2) Bugünkü görevler
      const { data: tasks, error: taskErr } = await dataClient
        .from("daily_tasks")
        .select("id, title, detail, icon, completed")
        .eq("user_id", userId)
        .eq("task_date", today)
        .order("sort_order", { ascending: true });

      if (taskErr) throw taskErr;

      // 3) Sıradaki canlı ders
      const { data: liveRows, error: liveErr } = await dataClient
        .from("live_classes")
        .select("id, subject, title, teacher_name, starts_at")
        .gt("starts_at", new Date().toISOString())
        .order("starts_at", { ascending: true })
        .limit(1);

      if (liveErr) throw liveErr;
      const liveClass: LiveClass | null = liveRows?.[0] ?? null;

      // 4) Son deneme sonucu
      const { data: examRows, error: examErr } = await dataClient
        .from("exam_attempts")
        .select("id, exam_name, correct_count, wrong_count, blank_count, net, taken_on")
        .eq("user_id", userId)
        .order("taken_on", { ascending: false })
        .limit(1);

      if (examErr) throw examErr;
      const rawExam = examRows?.[0] ?? null;
      const lastExam: ExamResult | null = rawExam
        ? {
            id: rawExam.id,
            exam_name: rawExam.exam_name,
            correct_count: rawExam.correct_count,
            wrong_count: rawExam.wrong_count,
            blank_count: rawExam.blank_count,
            net: Number(rawExam.net),
            taken_on: rawExam.taken_on,
          }
        : null;

      // 5) Eksik konular
      const { data: weakRows, error: weakErr } = await dataClient
        .from("weak_topics")
        .select("id, subject, topic, mastery_percent")
        .eq("user_id", userId)
        .order("mastery_percent", { ascending: true })
        .limit(6);

      if (weakErr) throw weakErr;

      setData({
        weekMinutes,
        studyDays,
        tasks: (tasks ?? []) as Task[],
        liveClass,
        lastExam,
        weakTopics: (weakRows ?? []) as WeakTopic[],
      });
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Veriler yüklenirken bir hata oluştu.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, reload: load };
}